import {
  AddIcon,
  Button,
  DeleteIcon,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  SearchInput,
  Table,
  type TableColumns,
} from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { ApplicationFormModal } from "../../components/Applications/ApplicationFormModal/ApplicationFormModal";
import { useConfirm } from "../../hooks/useConfirm";
import { Page } from "../../components/Page/Page";
import { PageToolbar } from "../../components/PageToolbar/PageToolbar";
import prim from "../../core/styles/helpers.module.scss";
import { showSuccessToast, showUserErrorToast } from "../../utils/toast";
import { isSafeHttpUrl } from "../../utils/url";
import css from "./ApplicationsPage.module.scss";

// Colocated fragment: the list only fetches the fields it renders.
const ApplicationsPage_Application = graphql(`
  fragment ApplicationsPage_Application on Application {
    uuid
    name
    clientId
    uri
    licensedOrganizationsConnection {
      totalCount
    }
  }
`);

const ApplicationsPageQuery = graphql(`
  query ApplicationsPage_Query {
    applications(options: { sort: { name: ASC } }) {
      ...ApplicationsPage_Application
    }
  }
`);

const ApplicationsPage_DeleteApplication = graphql(`
  mutation ApplicationsPage_DeleteApplication($uuid: ID!) {
    deleteApplications(where: { uuid: $uuid }) {
      nodesDeleted
    }
  }
`);

export function ApplicationsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [{ data }] = useQuery({ query: ApplicationsPageQuery });
  const [, deleteApplication] = useMutation(ApplicationsPage_DeleteApplication);
  const { confirm, ConfirmationDialog } = useConfirm();

  const apps = getFragmentData(ApplicationsPage_Application, data?.applications ?? []);
  type AppRow = (typeof apps)[number];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return apps;
    return apps.filter(
      (a) => a.name.toLowerCase().includes(s) || a.clientId.toLowerCase().includes(s),
    );
  }, [apps, q]);

  const onDelete = async (app: AppRow) => {
    const ok = await confirm({
      title: "Application löschen",
      message: `„${app.name}" wird entfernt. Der verknüpfte OAuth-Client bleibt bestehen.`,
      confirmText: "Löschen",
      cancelText: "Abbrechen",
    });
    if (!ok) {
      return;
    }
    const res = await deleteApplication({ uuid: app.uuid });
    if (res.error) {
      showUserErrorToast({ text: "Löschen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast("Application gelöscht");
  };

  const columns: TableColumns<AppRow>[] = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      size: 360,
      cell: ({ row }) => <span>{row.original.name}</span>,
    },
    {
      id: "clientId",
      header: "ClientId",
      size: 220,
      cell: ({ row }) => <code className={css.codepill}>{row.original.clientId}</code>,
    },
    {
      id: "uri",
      header: "URL",
      size: 280,
      cell: ({ row }) => {
        const uri = row.original.uri ?? "";
        return uri && isSafeHttpUrl(uri) ? (
          // biome-ignore lint/a11y/useKeyWithClickEvents: stops row navigation only
          <a
            href={uri}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {uri}
          </a>
        ) : (
          <span className={prim.muted}>—</span>
        );
      },
    },
    {
      id: "licensed",
      header: "Lizenzierte Orgs",
      accessorFn: (a) => a.licensedOrganizationsConnection?.totalCount ?? 0,
      type: "number",
      alignment: "right",
      size: 140,
    },
    {
      id: "actions",
      header: "",
      size: 56,
      cell: ({ row }) => (
        <Dropdown
          trigger={
            <Button
              hierarchy="tertiary"
              icon={<DotsHorizontalIcon />}
              ariaLabel="Aktionen"
              onClick={(e) => e.stopPropagation()}
            />
          }
        >
          <DropdownMenu
            data={[
              {
                label: "Details öffnen",
                onClick: () => navigate(`/applications/${row.original.uuid}`),
              },
              {
                label: "Löschen",
                color: "error",
                onClick: () => void onDelete(row.original),
              },
            ]}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <Page
      title="Applications"
      description="Produkte mit Lizenz und Zugriff. Jede Application referenziert einen OAuth-Client."
      actions={[
        {
          text: "Application erstellen",
          icon: <AddIcon />,
          hierarchy: "primary",
          callback: () => setAdding(true),
        },
      ]}
    >
      <PageToolbar>
        <SearchInput
          name="app-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name oder Client-ID …"
        />
      </PageToolbar>
      <Table<AppRow>
        columns={columns}
        data={filtered}
        isDesktop
        showSort
        showRowHover
        showShadow
        showBorderRadius
        onRowClick={(_, row) => navigate(`/applications/${row.original.uuid}`)}
      />
      {adding && (
        <ApplicationFormModal
          mode="create"
          onClose={() => setAdding(false)}
          onSaved={(uuid) => {
            setAdding(false);
            if (uuid) {
              navigate(`/applications/${uuid}`);
            }
          }}
        />
      )}
      <ConfirmationDialog />
    </Page>
  );
}
